import React, { useEffect, useState, useRef } from 'react';
import { RefundPolicyDto } from 'services/data-contracts';
import styled from 'styled-components';

const INPUT_PADDING_VERTICAL = 8;
const INPUT_PADDING_HORIZONTAL = 12;
const INPUT_HEIGHT = 40;

const StyledInputField = styled(InputField)`
  .MuiOutlinedInput-input {
    padding: ${INPUT_PADDING_VERTICAL}px ${INPUT_PADDING_HORIZONTAL}px !important;
  }

  .MuiInputBase-input {
    padding: ${INPUT_PADDING_VERTICAL}px ${INPUT_PADDING_HORIZONTAL}px !important;
  }

  .MuiInputBase-root {
    height: ${INPUT_HEIGHT}px;
  }
`;

// 개별 환불 정책 아이템 컴포넌트
const RefundPolicyItem = ({
  refundPolicy,
  onUpdate,
  isChanged = false,
}: {
  refundPolicy: RefundPolicyDto;
  onUpdate: (id: number, value: number) => void;
  isChanged?: boolean;
}) => {
  const [localValue, setLocalValue] = useState(refundPolicy.cancellationCharge?.toString() || '');

  const handleChange = (value: string) => {
    setLocalValue(value);
  };

  const handleBlur = () => {
    const numValue = Number(localValue);
    if (!isNaN(numValue)) {
      onUpdate((refundPolicy as any).id as number, numValue);
    }
  };

  useEffect(() => {
    setLocalValue(refundPolicy.cancellationCharge?.toString() || '');
  }, [refundPolicy.cancellationCharge]);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-start',
        gap: 20,
        alignItems: 'center',
        padding: '10px',
        borderRadius: '8px',
        backgroundColor: isChanged ? '#fff3cd' : 'transparent',
        border: isChanged ? '1px solid #ffeaa7' : '1px solid transparent',
      }}
    >
      <div style={{ minWidth: 150 }}>
        이용{' '}
        <span style={{ fontWeight: 600, fontSize: 20, color: 'var(--point-color)' }}>
          {refundPolicy.period}일
        </span>{' '}
        전
      </div>
      <div>
        <StyledInputField
          onChange={handleChange}
          onBlur={handleBlur}
          type="number"
          style={{ width: 265 }}
          value={localValue}
        />
      </div>
      <div>% 환불</div>
      {isChanged && (
        <div
          style={{
            fontSize: '12px',
            color: '#d68910',
            fontWeight: 'bold',
          }}
        >
          변경됨
        </div>
      )}
    </div>
  );
};

import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import ServiceHelper from 'stores/ServiceHelper';
import PageLayout from 'components/layouts/PageLayout';
import { InnerGroup, InnerSection } from 'components/layouts/CommonForm';
import { InputField } from 'saladsoft-primitive';
import Button from 'components/input/Button';
import ButtonWrapper from '../listings/ButtonWrapper';
import Text from 'components/commons/Text';

const RefundPage = observer(() => {
  const location = useLocation();
  const [refundPolicies, setRefundPolicies] = useState<RefundPolicyDto[]>([]);
  const [changedCount, setChangedCount] = useState(0);
  const originalPolicies = useRef<RefundPolicyDto[]>([]);
  const changedPolicies = useRef<Map<number, RefundPolicyDto>>(new Map());

  const fetchRefundPolicies = () => {
    ServiceHelper.adminRefundPolicyService?.getRefundPolicies1().then((res) => {
      setRefundPolicies(res.data);
      originalPolicies.current = res.data;
      changedPolicies.current.clear(); // 새로 가져올 때 변경된 정책 초기화
      setChangedCount(0);
    });
  };

  const handleUpdateRefundPolicy = (id: number, value: number) => {
    // 원본 정책 찾기
    const originalPolicy = originalPolicies.current.find((policy) => (policy as any).id === id);

    if (originalPolicy) {
      // 원본 값과 비교해서 변경되었는지 확인
      if (originalPolicy.cancellationCharge !== value) {
        // 변경된 경우 changedPolicies에 추가/업데이트
        changedPolicies.current.set(id, {
          ...originalPolicy,
          cancellationCharge: value,
        });
      } else {
        // 원본과 같은 값으로 되돌아간 경우 changedPolicies에서 제거
        changedPolicies.current.delete(id);
      }

      // UI 업데이트를 위해 현재 상태 재계산
      const updatedPolicies = refundPolicies.map((policy) => {
        const changedPolicy = changedPolicies.current.get((policy as any).id as number);
        return changedPolicy || policy;
      });
      setRefundPolicies(updatedPolicies);
      setChangedCount(changedPolicies.current.size);
    }
  };

  const handleSaveRefundPolicies = async () => {
    // 변경된 정책들을 배열로 변환
    const changedPoliciesArray = Array.from(changedPolicies.current.values());

    if (changedPoliciesArray.length === 0) {
      console.log('변경된 정책이 없습니다.');
      return;
    }

    console.log('변경된 정책들:', changedPoliciesArray);

    try {
      // 모든 정책 업데이트를 병렬로 실행
      const updatePromises = changedPoliciesArray.map((policy) =>
        ServiceHelper.adminRefundPolicyService?.updateRefundPolicy((policy as any).id as number, {
          period: policy.period as number,
          cancellationCharge: policy.cancellationCharge as number,
        }),
      );

      const results = await Promise.all(updatePromises);

      // 모든 요청이 성공했는지 확인
      const allSuccessful = results.every((res) => res?.status === 200);

      if (allSuccessful) {
        // 모든 업데이트가 성공한 경우에만 상태 초기화
        changedPolicies.current.clear();
        setChangedCount(0);
        fetchRefundPolicies(); // 최신 데이터 다시 가져오기
        console.log('모든 환불 정책이 성공적으로 저장되었습니다.');
      } else {
        console.error('일부 환불 정책 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('환불 정책 저장 중 오류가 발생했습니다:', error);
    }
  };
  useEffect(() => {
    fetchRefundPolicies();
  }, [location.search]);

  return (
    <PageLayout title="환불 정책">
      <InnerSection>
        <InnerGroup>
          <div>
            <Text type="H3">환불 정책</Text>
            <Text type="H4">환불 정책을 설정해주세요.</Text>
            {changedCount > 0 && (
              <div
                style={{
                  marginTop: '10px',
                  padding: '8px 12px',
                  backgroundColor: '#e8f5e8',
                  border: '1px solid #28a745',
                  borderRadius: '4px',
                  fontSize: '14px',
                  color: '#155724',
                }}
              >
                변경된 정책: {changedCount}개
              </div>
            )}
          </div>
          <div>
            {refundPolicies.map((refundPolicy) => (
              <RefundPolicyItem
                key={(refundPolicy as any).id}
                refundPolicy={refundPolicy}
                onUpdate={handleUpdateRefundPolicy}
                isChanged={changedPolicies.current.has((refundPolicy as any).id as number)}
              />
            ))}
          </div>
        </InnerGroup>
      </InnerSection>
      <ButtonWrapper>
        <Button
          onClick={handleSaveRefundPolicies}
          isValid={changedCount > 0}
          style={{
            opacity: changedCount === 0 ? 0.5 : 1,
            cursor: changedCount === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          저장 {changedCount > 0 && `(${changedCount}개 변경)`}
        </Button>
      </ButtonWrapper>
    </PageLayout>
  );
});

export default RefundPage;
